import React from "react";

import { FigureBlock as FigureBlockValue } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { RichText } from "../RichText";

import breakpoints from "../../styles/breakpoint.module.scss";
import styles from "./Figure.module.scss";

type Size = "text" | "wide" | "full";

// Approximate rendered width per size, so the browser picks a rendition that
// fits the column instead of assuming the image spans the whole viewport.
const SIZES: Record<Size, string> = {
    text: `(max-width: ${breakpoints.mobile}) 100vw, (max-width: ${breakpoints.tablet}) 80vw, 46vw`,
    wide: `(max-width: ${breakpoints.tablet}) 100vw, 78vw`,
    full: "100vw",
};

interface Props {
    value: FigureBlockValue;
}

export const Figure = ({ value }: Props) => {
    if (!value.image) return null;
    const size: Size = value.size || "text";
    const isVector = Object.values(value.image.renditions).some(
        (r) => r.mimetype === "image/svg+xml",
    );
    const imageAttrs = value.alt_text ? { alt: value.alt_text } : undefined;
    const caption = value.caption;
    const hasCaption =
        caption?.blocks.some((b) => b.text.trim() !== "") ?? false;
    return (
        <figure className={styles.root}>
            <div
                className={concatClassNames([
                    styles.image,
                    styles[size],
                    isVector ? styles.vector : null,
                ])}
            >
                <ImageChooserBlock
                    value={value.image}
                    imageAttrs={imageAttrs}
                    sizes={SIZES[size]}
                    // Inlined SVG markup ignores alt text and its fixed width
                    // overflows narrow screens, so always render an <img>
                    useImageData={false}
                />
            </div>
            {hasCaption && caption && (
                <figcaption
                    className={concatClassNames([styles.caption, styles[size]])}
                >
                    <RichText
                        value={caption}
                        blockOverrides={{
                            unstyled: {
                                element: "p",
                                props: { className: styles.captionText },
                            },
                        }}
                    />
                </figcaption>
            )}
        </figure>
    );
};
